import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchMyLegalConsentStatus } from '../api/profileApi';
import { needsLegalConsent } from '../lib/legalConsentGate';
import { SESSION_RESTORE_MS, waitForSupabaseSession } from '../lib/authSession';
import {
  beginSignupFlow,
  clearAuthSessionFlags,
  hasLocalePreferences,
  isGuestStorage,
  isLoggedInStorage,
} from '../lib/onboardingStorage';
import { getSupabase, isSupabaseConfigured } from '../lib/supabase';

/** 로그인 유저가 consent 미완료면 /onboarding/oauth-consent 로 (TabLayout 등 본진입 가드). */
function LegalConsentGuard({ children }) {
  const navigate = useNavigate();
  /** @type {['loading' | 'allowed', (v: 'loading' | 'allowed') => void]} */
  const [phase, setPhase] = useState('loading');

  useEffect(() => {
    if (isGuestStorage() || !isLoggedInStorage()) {
      setPhase('allowed');
      return undefined;
    }

    let cancelled = false;

    async function run() {
      if (!isSupabaseConfigured) {
        if (!cancelled) setPhase('allowed');
        return;
      }
      const supabase = getSupabase();
      if (!supabase) {
        if (!cancelled) setPhase('allowed');
        return;
      }

      const session = await waitForSupabaseSession(supabase, {
        maxMs: SESSION_RESTORE_MS,
        expectSession: isLoggedInStorage() && !isGuestStorage(),
      });
      if (cancelled) return;

      if (!session?.user) {
        // splash로내면 splash→home 과 ping-pong 루프 — Splash 와 동일하게 플래그 정리 후 로그인으로
        clearAuthSessionFlags();
        if (hasLocalePreferences()) {
          beginSignupFlow();
          navigate('/onboarding/auth', { replace: true });
        } else {
          navigate('/onboarding', { replace: true });
        }
        return;
      }

      const status = await fetchMyLegalConsentStatus(session.user);
      if (cancelled) return;

      if (needsLegalConsent(status)) {
        navigate('/onboarding/oauth-consent', { replace: true });
        return;
      }

      setPhase('allowed');
    }

    void run();

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  if (phase === 'loading') {
    return (
      <div className="mingo-page mingo-page--stack" style={{ padding: 48, textAlign: 'center' }} aria-busy="true">
        <p>…</p>
      </div>
    );
  }

  return children;
}

export default LegalConsentGuard;
